import { ReportUnavailable } from '../ReportUnavailable';
import React from 'react';
import Link from 'next/link';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Typography } from '@vibTheme/components/_base/Typography';
import { MetricCard } from '../PaymentConversion/shared';
import {
  RecurringRevenueFiltersInput,
  useRecurringRevenue,
} from './useRecurringRevenue';
import {
  describePopulation,
  resolvePopulation,
} from '@utils/resolvePopulation';
import {
  MrrCoverageNote,
  describeAtRiskMrr,
  formatCoveredMoney,
  formatMoney,
  formatRate,
} from './shared';
import { ReportLinkFilters, reportLinkHref } from '@utils/reportLinkParams';

type Props = { filters: RecurringRevenueFiltersInput };

export const RECURRING_REPORT_PATH = '/reports/recorrencia';

/**
 * Carries the selection into the detail screen.
 *
 * A bare link would open it on its own default period and all communities, so the series would not explain
 * the cards that were clicked. The named period travels when there is one; the resolved dates travel
 * always, so a custom range survives too.
 */
export const recurringReportHref = (filters: ReportLinkFilters) =>
  reportLinkHref(RECURRING_REPORT_PATH, filters);

/**
 * Dashboard summary: what the recurring base is worth today, and what is already leaving it.
 *
 * Three cards, not nine. The month-by-month series lives on the detail screen - the root page already
 * carries the sales cards, the history chart and the conversion card, and a nine-column table on top of
 * that buries the numbers a producer opens the page for.
 *
 * IMPORTANT: the first two cards describe RIGHT NOW, not the selected period, and they say so in their
 * captions. They come from the snapshot, which does not depend on the filter. Only the churn card is about
 * the period. Putting a period-independent number under a period filter without labelling it is how a
 * dashboard starts lying quietly.
 */
export function RecurringRevenueSummary({ filters }: Props) {
  const { snapshot, coverage, totals, isLoading, hasFailed, hasLoaded } =
    useRecurringRevenue(filters);
  const atRisk = describeAtRiskMrr(snapshot, coverage, formatMoney);
  const population = resolvePopulation(snapshot, coverage);

  return (
    <section className="pb-main">
      <div className="pb-main flex items-baseline justify-between gap-main">
        <div>
          <Typography variant="h2">Receita recorrente</Typography>
          <Typography className="text-sm text-lightBlack">
            Quanto a base de assinantes vale por mês, e quanto dela está saindo
          </Typography>
        </div>
        <Link
          href={recurringReportHref(filters)}
          className="text-sm text-primary whitespace-nowrap flex items-center gap-[4px]"
        >
          Ver detalhes
          <ArrowForwardIcon fontSize="small" />
        </Link>
      </div>

      {/*
       * A failure would otherwise draw R$ 0,00 with a caption saying "hoje", which reads as a base worth
       * nothing rather than as a number that could not be fetched.
       */}
      {hasFailed ? (
        <ReportUnavailable />
      ) : (
        <>
          {/*
        Above the cards on purpose. Read after the figures, a coverage note only explains a conclusion the
        producer has already drawn.
      */}
          {hasLoaded && !isLoading && (
            <MrrCoverageNote
              coverage={coverage}
              instant="current"
              snapshot={snapshot}
            />
          )}
          <div className="grid gap-main md:grid-cols-2 2xl:grid-cols-3">
            {/*
          "MRR projetado", not "MRR". This card is the base as it stands NOW priced by what each subscription is
          entitled to keep charging - a forward-looking figure. The series on the detail screen is "MRR": a
          measured level, per period, at what each subscription was actually paying then. Two questions, two
          names; calling both of them MRR is what made a coproducer who left look like a discrepancy instead of
          like someone who is no longer entitled.
        */}
            <MetricCard
              title="MRR projetado"
              isLoading={isLoading}
              value={formatCoveredMoney(
                snapshot.mrr,
                coverage,
                'current',
                formatMoney,
                snapshot,
              )}
              caption={`hoje · ${describePopulation(population)}`}
              indicator={null}
              help="A receita mensal que a sua base de assinantes ativos deve renovar, contada HOJE — não no período selecionado. Cada assinatura entra pelo valor da última cobrança dela, convertido para 30 dias: um plano trimestral de R$ 300 entra como R$ 100. É projeção, não histórico: assume que a base de hoje renova, e conta só o que você ainda tem direito a receber. Para o valor efetivamente medido mês a mês, veja a série em Ver detalhes. Só ofertas de assinatura contam; pack, chamada de vídeo e oferta personalizada são venda avulsa e ficam de fora. Assinatura de cortesia, sem cobrança, também não aparece."
            />
            <MetricCard
              title="MRR em risco"
              isLoading={isLoading}
              value={atRisk.value}
              caption={`hoje · ${atRisk.caption}`}
              indicator={null}
              help="Parte do MRR que pertence a assinantes que já desligaram a renovação automática. Eles continuam ativos até o acesso vencer, então esse valor ainda está no MRR — mas vai sair, salvo se voltarem atrás. É a única perda que dá para ver antes de acontecer. A cobertura declarada acima é da base INTEIRA e não garante esta fatia: se justamente as assinaturas sem período conhecido forem as que desligaram a renovação, este valor sai menor sem que a cobertura geral acuse."
            />
            <MetricCard
              title="Churn no período"
              isLoading={isLoading}
              value={formatRate(totals.churnRate)}
              caption={`${totals.churnedSubscribers} saíram · ${totals.newSubscribers} entraram`}
              indicator={null}
              help="De todos os assinantes que a comunidade teve no período — os que já estavam no começo mais os que entraram — a fatia que saiu. O denominador inclui quem entrou de propósito: sem isso, alguém que entra e sai dentro do mesmo período apareceria na conta de cima sem aparecer na de baixo, e uma comunidade pequena poderia mostrar churn acima de 100%."
            />
          </div>
        </>
      )}
    </section>
  );
}
